import {
  bareModelId,
  bareModelPersona,
  TEMPLATE_PERSONAS,
  type Persona,
} from "@workspace/personas";
import { getPresetModelIds } from "@workspace/model-presets";
import { Bot, ChevronDown, Loader2, Plus, Trash2 } from "lucide-react";

import type { Model } from "../../types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui";
import { SelectorModelBadges } from "./model-badges";
import { cn } from "../../utils";

interface PersonaSelectorProps {
  /** The user's saved personas (source: "user"). */
  personas: Persona[];
  /** Live models from the LB — drives the bare-model tail + availability checks. */
  models: Model[];
  /** Currently active persona id (settings.personaId), or null for none. */
  selectedPersonaId: string | null;
  /** Fired when the user picks a persona (resolved; bare models become personas). */
  onPersonaChange: (persona: Persona) => void;
  /** Fired when the user clicks "Create your own". */
  onCreateNew: () => void;
  /** Edit a persona (opens the builder seeded from it). Templates → "customise". */
  onEditPersona?: (persona: Persona) => void;
  /** Delete one of the user's own personas. */
  onDeletePersona?: (personaId: string) => void;
  isLoading: boolean;
  error: string | null;
  disabled?: boolean;
  variant?: "default" | "minimal";
  fullWidth?: boolean;
  triggerClass?: string;
}

function modelDisplayName(modelId: string): string {
  const name = modelId.split("/").pop() ?? modelId;
  return name
    .replace(/-GGUF$/i, "")
    .replace(/-Instruct$/i, "")
    .replace(/_/g, " ")
    .replace(/-/g, " ");
}

/** Resolve the display label for the trigger from the active persona/model. */
function triggerLabel(
  selectedPersonaId: string | null,
  personas: Persona[],
  models: Model[],
): { icon?: string; name: string } {
  if (selectedPersonaId) {
    const user = personas.find((p) => p.id === selectedPersonaId);
    if (user) return { icon: user.icon, name: user.name };
    const template = TEMPLATE_PERSONAS.find((p) => p.id === selectedPersonaId);
    if (template) return { icon: template.icon, name: template.name };
    // Bare model persona id has the form `model:<id>`.
    if (selectedPersonaId.startsWith("model:")) {
      const id = selectedPersonaId.slice("model:".length);
      return { name: modelDisplayName(id) };
    }
  }
  // Fall back to the first available model name.
  const first = models[0];
  return { name: first ? modelDisplayName(first.id) : "Select persona" };
}

export function PersonaSelector({
  triggerClass: _triggerClass = "",
  personas,
  models,
  selectedPersonaId,
  onPersonaChange,
  onCreateNew,
  onDeletePersona,
  isLoading,
  error,
  disabled,
  variant = "default",
  fullWidth = false,
}: PersonaSelectorProps) {
  // `minimal` is icon-only: it sits in the composer's control row beside
  // history/attach/settings, and a variable-width name pushed those around as
  // the persona changed. Sized h-9 w-9 to match them exactly.
  //
  // No `relative` wrapper is needed to stop the open menu shifting layout —
  // DropdownMenuContent renders through a Radix Portal at the document root,
  // so it is outside this element's flow entirely.
  // Base shape per variant, then the caller's decoration LAST. Merged with
  // `cn` so an overriding utility actually replaces the base one — passing
  // `rounded-full` against a base `rounded-md` otherwise emits both and leaves
  // the result to source order.
  const triggerClass = cn(
    variant === "minimal"
      ? "inline-flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/50 disabled:opacity-50"
      : "inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-50",
    fullWidth && "w-full justify-between",
    _triggerClass,
  );

  const liveModelIds = new Set(models.map((m) => m.id));
  const label = triggerLabel(selectedPersonaId, personas, models);

  // Templates whose pinned model is currently available, sorted so the
  // selector groups read top-to-bottom: Yours → Templates → Create → Bare.
  const availableTemplates = TEMPLATE_PERSONAS;

  // Bare models: only the curated, whitelisted models from MODEL_PRESETS (the
  // same source that drives the manual config + template metadata) — not every
  // raw model the LB reports. Preserve preset declaration order.
  const presetOrder = getPresetModelIds();
  const bareModels = presetOrder
    .map((id) => models.find((m) => m.id === id))
    .filter((m): m is Model => m !== undefined);

  if (error) {
    return (
      <button
        type="button"
        disabled
        className={cn(triggerClass)}
        // The icon-only box has no room for the sentence, so the message moves
        // to the tooltip and the glyph turns destructive.
        {...(variant === "minimal"
          ? {
              "aria-label": "Error loading models",
              title: "Error loading models",
            }
          : {})}
      >
        {variant === "minimal" ? (
          <Bot className="h-4 w-4 md:h-5 md:w-5" />
        ) : (
          <span className="text-sm text-destructive">Error loading models</span>
        )}
      </button>
    );
  }

  const handleSelectPersona = (persona: Persona) => {
    // If a persona's pinned model is unavailable, still apply it — the request
    // layer will surface the issue; we keep the params + prompt usable.
    onPersonaChange(persona);
  };

  const handleSelectBareModel = (model: Model) => {
    onPersonaChange(
      bareModelPersona({ id: model.id, capabilities: model.capabilities }),
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        // Deliberately NOT disabled while models load. A slow model fetch is
        // exactly when someone wants the menu — their own personas are already
        // in hand and do not depend on the request. Disabling it made the whole
        // control dead for as long as the fetch took, which read as a frozen
        // UI rather than a pending list.
        disabled={disabled}
        className={cn(triggerClass)}
        // Icon-only, so the name has to be exposed to assistive tech and to a
        // hover tooltip — otherwise which persona is active becomes invisible.
        {...(variant === "minimal"
          ? { "aria-label": `Persona: ${label.name}`, title: label.name }
          : {})}
      >
        {isLoading ? (
          variant === "minimal" ? (
            <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
          ) : (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              <span className="text-sm">Loading...</span>
            </>
          )
        ) : variant === "minimal" ? (
          // The persona's own emoji when it has one; a neutral glyph when it
          // does not (bare models carry no icon), so the control never
          // collapses to an empty box.
          label.icon ? (
            <span className="text-base md:text-lg leading-none">
              {label.icon}
            </span>
          ) : (
            <Bot className="h-4 w-4 md:h-5 md:w-5" />
          )
        ) : (
          <>
            <span className="text-sm inline-flex items-center gap-1">
              {label.icon && <span>{label.icon}</span>}
              {label.name}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === "minimal" ? "start" : "end"}
        className="w-80 max-h-[28rem] overflow-y-auto"
      >
        {/* Yours */}
        {personas.length > 0 && (
          <>
            <DropdownMenuLabel>Your personas</DropdownMenuLabel>
            {personas.map((p) => (
              <DropdownMenuItem
                key={p.id}
                onClick={() => handleSelectPersona(p)}
                className="flex items-start gap-2 py-2"
              >
                <div className="flex flex-1 flex-col items-start gap-0.5">
                  <span className="font-medium inline-flex items-center gap-1.5">
                    {p.icon && <span>{p.icon}</span>}
                    {p.name}
                  </span>
                  {p.description && (
                    <span className="text-xs text-muted-foreground">
                      {p.description}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {modelDisplayName(p.modelId)}
                    {!liveModelIds.has(p.modelId) && " · model unavailable"}
                  </span>
                </div>
                {onDeletePersona && (
                  <div className="flex items-center gap-0.5 pt-0.5">
                    <button
                      type="button"
                      aria-label="Delete persona"
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeletePersona(p.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        {/* Templates */}
        <DropdownMenuLabel>Select a model or persona</DropdownMenuLabel>
        {availableTemplates.map((p) => (
          <DropdownMenuItem
            key={p.id}
            onClick={() => handleSelectPersona(p)}
            className="flex flex-col items-start gap-0.5 py-2"
          >
            <span className="font-medium inline-flex items-center gap-1.5">
              {p.icon && <span>{p.icon}</span>}
              {p.name}
            </span>
            {p.description && (
              <span className="text-xs text-muted-foreground">
                {p.description}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {modelDisplayName(p.modelId)}
              {!liveModelIds.has(p.modelId) && " · model unavailable"}
            </span>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        {/* Create your own */}
        <DropdownMenuItem
          onClick={onCreateNew}
          className="gap-2 py-2 font-medium text-primary"
        >
          <Plus className="h-4 w-4" />
          Create your own
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Bare models */}
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Models
        </DropdownMenuLabel>
        {bareModels.length === 0 ? (
          <div className="px-2 py-4 text-center text-sm text-muted-foreground">
            No models available
          </div>
        ) : (
          bareModels.map((model) => {
            const isSelected = selectedPersonaId === bareModelId(model.id);
            return (
              <DropdownMenuItem
                key={model.id}
                onClick={() => handleSelectBareModel(model)}
                className={
                  "flex flex-col items-start gap-1 py-2 " +
                  (isSelected ? "bg-accent/50" : "")
                }
              >
                <span className="font-medium">
                  {modelDisplayName(model.id)}
                </span>
                <SelectorModelBadges model={model} />
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
